// "This person may already be here."
//
// Shown while somebody is being added, and in the directory for duplicates that
// already exist. Deliberately the SAME component in both places: if the warning
// and the review panel described a match differently, whoever is cleaning up
// would have to work out which one to believe.
//
// IT NEVER BLOCKS. Two people genuinely can share a name, and refusing to add
// the second one is a worse failure than a duplicate row — it stops somebody
// being paid. Everything here is information for a human to act on.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, UserCheck, UserX, Mailbox } from 'lucide-react';
import type { DuplicateMatch, DuplicateReason } from '@/lib/hrApi';

/** Why we think it is the same person, in words HR can judge. A score would be
 *  something they cannot argue with; a reason they can. */
export const reasonLabel = (t: any, reason: DuplicateReason): string => {
  switch (reason) {
    case 'same_email':
      return t('users.dup_same_email', { defaultValue: 'same email address' });
    case 'similar_email':
      return t('users.dup_similar_email', { defaultValue: 'almost the same email — check for a typo' });
    default:
      return t('users.dup_same_name', { defaultValue: 'same name' });
  }
};

/** A shared mailbox used as one person's account. A DIFFERENT problem from a
 *  duplicate, so it gets its own words: the risk is not a second row, it is that
 *  the person's data sits in a mailbox several people read and any of them can
 *  password-reset into the account. */
export const SharedMailboxWarning: React.FC = () => {
  const { t } = useTranslation(['employees']);
  return (
    <div className="rounded-lg border border-warning-strong/30 bg-warning-soft p-3">
      <div className="flex items-start gap-2">
        <Mailbox className="w-4 h-4 mt-0.5 text-warning-strong shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {t('users.shared_mailbox', {
              defaultValue: 'That looks like a shared mailbox, not one person',
            })}
          </p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {t('users.shared_mailbox_why', {
              defaultValue:
                'Anyone who reads that mailbox could reset the password and see this person’s details. Use their own work address where you can.',
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export const DuplicatePersonWarning: React.FC<{
  matches: DuplicateMatch[];
  /** Rendered under the list — e.g. "Invite anyway" stays enabled. */
  children?: React.ReactNode;
}> = ({ matches, children }) => {
  const { t } = useTranslation(['employees']);
  if (!matches.length) return null;

  return (
    <div className="rounded-lg border border-warning-strong/30 bg-warning-soft p-3 space-y-2">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 text-warning-strong shrink-0" />
        <div className="min-w-0 space-y-2">
          <p className="text-sm font-medium text-foreground">
            {t('users.dup_heading', {
              defaultValue: 'This person may already be in your directory',
              count: matches.length,
            })}
          </p>
          <ul className="space-y-1.5">
            {matches.slice(0, 5).map((m) => (
              <li key={m.employeeId} className="text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5">
                {/* No login is the strongest hint that THIS is the record that
                    was mistyped — the invite never reached anybody. */}
                {m.hasLogin
                  ? <UserCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  : <UserX className="w-3.5 h-3.5 text-warning-strong shrink-0" />}
                <span className="font-medium text-foreground">{m.name || m.email}</span>
                <span className="text-muted-foreground break-all">{m.email}</span>
                <span className="text-xs text-muted-foreground">· {reasonLabel(t, m.reason)}</span>
                {!m.hasLogin && (
                  <span className="text-xs text-warning-strong">
                    · {t('users.dup_never_signed_in', { defaultValue: 'never signed in' })}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {matches.length > 5 && (
            <p className="text-xs text-muted-foreground">
              {t('users.dup_more', { defaultValue: 'and {{n}} more', n: matches.length - 5 })}
            </p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default DuplicatePersonWarning;
